import { memo, useState } from "react";
import { useI18n } from "@/i18n";
import type { Locale } from "@/i18n";
import { useSessionStore } from "@/application/stores/sessionStore";
import { useRouter } from "@/application/stores/routerStore";
import { updateUser } from "@/infrastructure/user/userService";
import { refreshToken, clearToken } from "@/infrastructure/auth/authService";
import { colors, typography, transitions } from "@/design-system/tokens";

export const ChangePasswordScreen = memo(function ChangePasswordScreen() {
  const { t, locale, setLocale } = useI18n();
  const userId = useSessionStore(s => s.userId);
  const setFromToken = useSessionStore(s => s.setFromToken);
  const { navigate } = useRouter();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const checks = {
    min8: password.length >= 8,
    lower: /[a-z]/.test(password),
    upper: /[A-Z]/.test(password),
    number: /\d/.test(password),
    special: /[^a-zA-Z0-9]/.test(password),
  };

  const allValid = checks.min8 && checks.lower && checks.upper && checks.number && checks.special;
  const matches = password === confirm && confirm.length > 0;
  const canSubmit = allValid && matches;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError("");
    try {
      await updateUser(userId, { password });
      const newToken = await refreshToken();
      setFromToken(newToken);
    } catch {
      setError(t.users.errorValidation);
      setSaving(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", padding: "32px", position: "relative" }}>
      {/* Locale switcher */}
      <div style={{ position: "absolute", top: "20px", right: "24px", display: "flex", gap: "4px" }}>
        {(["es", "en"] as Locale[]).map(l => (
          <button key={l} onClick={() => setLocale(l)} style={{ fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.bold, padding: "5px 10px", borderRadius: "6px", border: "none", cursor: "pointer", background: locale === l ? "#111" : "#f0f0f0", color: locale === l ? "#fff" : "#888", transition: `all ${transitions.fast}` }}>
            {l.toUpperCase()}
          </button>
        ))}
      </div>
      <div style={{ width: "100%", maxWidth: "420px", display: "flex", flexDirection: "column", alignItems: "center", gap: "28px" }}>
        {/* Icon */}
        <div style={{ width: "52px", height: "52px", background: colors.primary, borderRadius: "14px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>

        {/* Title */}
        <div style={{ textAlign: "center" }}>
          <h1 style={{ fontFamily: typography.fontFamily, fontSize: typography.size["2xl"], fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>
            {t.changePassword.title}
          </h1>
          <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#888", margin: "8px 0 0" }}>
            {t.changePassword.subtitle}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ width: "100%", display: "flex", flexDirection: "column", gap: "16px" }}>
          {/* New password */}
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={labelStyle}>{t.changePassword.newPassword} *</label>
            <div style={{ position: "relative" }}>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="new-password"
                style={{ ...inputStyle, paddingRight: "44px" }}
              />
              <button type="button" onClick={() => setShowPassword(v => !v)} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#aaa", display: "flex" }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
              </button>
            </div>
            {/* Validation checklist */}
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginTop: "4px" }}>
              <Check ok={checks.min8} label={t.users.pwMin8} />
              <Check ok={checks.lower} label={t.users.pwLower} />
              <Check ok={checks.upper} label={t.users.pwUpper} />
              <Check ok={checks.number} label={t.users.pwNumber} />
              <Check ok={checks.special} label={t.users.pwSpecial} />
            </div>
          </div>

          {/* Confirm password */}
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={labelStyle}>{t.changePassword.confirmPassword} *</label>
            <input
              type={showPassword ? "text" : "password"}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              onPaste={e => e.preventDefault()}
              autoComplete="new-password"
              style={{ ...inputStyle, borderColor: confirm.length > 0 && !matches ? colors.error : "#e5e5e5" }}
            />
            {confirm.length > 0 && !matches && (
              <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: colors.error }}>{t.changePassword.mismatch}</span>
            )}
          </div>

          {/* Error */}
          {error && <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: colors.error }}>{error}</span>}

          {/* Submit */}
          <button
            type="submit"
            disabled={!canSubmit || saving}
            style={{
              fontFamily: typography.fontFamily,
              fontSize: typography.size.sm,
              fontWeight: typography.weight.bold,
              color: "#fff",
              background: canSubmit && !saving ? "#111" : "#ccc",
              border: "none",
              borderRadius: "10px",
              padding: "14px",
              cursor: canSubmit && !saving ? "pointer" : "not-allowed",
              transition: `background ${transitions.fast}`,
              marginTop: "8px",
            }}
            onMouseEnter={e => { if (canSubmit && !saving) (e.currentTarget as HTMLButtonElement).style.background = "#333"; }}
            onMouseLeave={e => { if (canSubmit && !saving) (e.currentTarget as HTMLButtonElement).style.background = "#111"; }}
          >
            {saving ? "..." : t.changePassword.submit}
          </button>
        </form>

        {/* Back to login */}
        <button
          onClick={() => { clearToken(); navigate("login"); }}
          style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#999", background: "transparent", border: "none", cursor: "pointer", marginTop: "-8px" }}
        >
          {t.login.backHome}
        </button>
      </div>
    </div>
  );
});

function Check({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: ok ? "#16a34a" : "#bbb" }}>
        {ok ? "✓" : "✗"}
      </span>
      <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: ok ? "#16a34a" : "#bbb" }}>
        {label}
      </span>
    </div>
  );
}

const labelStyle: React.CSSProperties = { fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" };
const inputStyle: React.CSSProperties = { fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1.5px solid #e5e5e5", borderRadius: "10px", padding: "12px 16px", outline: "none", width: "100%", boxSizing: "border-box" };
